import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { showErrorToast, showSuccessToast } from "../../components/Toast";
import { getAuthToken } from "../../lib/api/tokenStore";
import useWallet from "./useWallet";
import useTransferRecipients from "./useTransferRecipients";
import useWithdrawalRequest from "./useWithdrawalRequest";
import FinanceShell from "./components/FinanceShell";
import AmountEntrySheet from "./components/AmountEntrySheet";
import FinanceEmptyState from "./components/FinanceEmptyState";
import {
  RecipientCardsSkeleton,
  SummaryLinesSkeleton,
  WalletCardSkeleton,
} from "./components/FinanceSkeletons";
import { FINANCE_COPY, FINANCE_EMPTY_STATES, serverMessage, withdrawalRequestedMessage } from "./financeCopy";
import { formatMoney, moneyOf, parseAmount } from "./financeMoney";
import { FINANCE_PATH, WITHDRAWALS_PATH } from "./financeRoutes";

import "../checkout/styles.css";
import "./styles.css";

const WithdrawRoute = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const enabled = Boolean(getAuthToken());

  const currencyParam = params.get("currency");
  const amountParam = params.get("amount");

  const wallet = useWallet({ currencyCode: currencyParam || undefined, enabled });
  const recipients = useTransferRecipients({ enabled });

  const [amountOverride, setAmountOverride] = useState(null);
  const [recipientId, setRecipientId] = useState(null);
  const [managing, setManaging] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const requestedAmount = amountParam === null ? null : parseAmount(amountParam);
  const amount = amountOverride ?? requestedAmount ?? wallet.balance.value;
  const currency = wallet.currencyCode;

  const withdrawal = useWithdrawalRequest({
    transferRecipientId: recipientId,
    amount,
    currency,
  });

  const money = useMemo(() => moneyOf(amount, currency), [amount, currency]);
  const overBalance = amount > wallet.balance.value;

  const handleSubmit = async () => {
    if (!recipientId || !(amount > 0)) {
      showErrorToast(FINANCE_COPY.chooseAccountAndAmount);

      return;
    }

    const outcome = await withdrawal.submit();

    if (outcome.status === "error") {
      showErrorToast(serverMessage(outcome.error, FINANCE_COPY.withdrawalFailed));

      return;
    }

    showSuccessToast(withdrawalRequestedMessage(money), "Withdrawal Requested");
    navigate(WITHDRAWALS_PATH, { replace: true });
  };

  const handleRemove = async (recipient) => {
    const outcome = await recipients.remove(recipient.id);

    if (outcome.status === "error") {
      showErrorToast(serverMessage(outcome.error, FINANCE_COPY.recipientDeleteFailed));

      return;
    }

    if (recipientId === recipient.id) setRecipientId(null);
  };

  const toggleManage = () => {
    setManaging((current) => !current);
    setRecipientId(null);
  };

  const submitDisabled =
    withdrawal.submitting || withdrawal.evaluating || overBalance || !recipientId || !(amount > 0);

  return (
    <FinanceShell
      title="Withdraw"
      backTo={FINANCE_PATH}
    >
      {wallet.isLoading ? (
        <WalletCardSkeleton />
      ) : wallet.isError && !wallet.wallet ? (
        <FinanceEmptyState state={FINANCE_EMPTY_STATES.walletError} onRetry={wallet.refetch} />
      ) : (
        <section className="fin-wallet fin-wallet-compact">
          <p className="fin-wallet-label">
            {amount > 0 ? "Withdrawing" : "Current Balance"}
          </p>

          <p className="fin-wallet-amount">
            {amount > 0 ? formatMoney(money) : formatMoney(wallet.balance)}
          </p>

          <div className="fin-wallet-actions">
            <button
              type="button"
              className="fin-wallet-btn fin-wallet-btn-outline"
              onClick={() => setSheetOpen(true)}
            >
              Change
            </button>
          </div>

          {overBalance && (
            <p className="fin-wallet-warn">
              That is more than your balance of {formatMoney(wallet.balance)}.
            </p>
          )}
        </section>
      )}

      <section className="sy-flow-card">
        <div className="sy-flow-card-head">
          <h2 className="sy-flow-card-title">Withdrawal Account</h2>

          {recipients.recipients.length > 0 && (
            <button type="button" className="fin-chip" onClick={toggleManage}>
              {managing ? "Done" : "Manage"}
            </button>
          )}
        </div>

        <div className="sy-flow-card-body">
          {recipients.isLoading ? (
            <RecipientCardsSkeleton />
          ) : recipients.recipients.length ? (
            <ul className="fin-choices">
              {recipients.recipients.map((recipient) => (
                <li key={recipient.id}>
                  {managing ? (
                    <div className="fin-choice">
                      <span className="fin-choice-body">
                        <b>{recipient.providerName}</b>
                        <span>{recipient.maskedAccountNumber}</span>
                        <span>{recipient.accountName}</span>
                      </span>

                      <button
                        type="button"
                        className="fin-choice-trash"
                        aria-label={`Remove ${recipient.label}`}
                        disabled={recipients.removingId === recipient.id}
                        onClick={() => handleRemove(recipient)}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <label
                      className={`fin-choice${recipientId === recipient.id ? " is-active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="withdrawal-account"
                        checked={recipientId === recipient.id}
                        onChange={() => setRecipientId(recipient.id)}
                      />

                      <span className="fin-choice-body">
                        <b>{recipient.providerName}</b>
                        <span>{recipient.maskedAccountNumber}</span>
                        <span>{recipient.accountName}</span>
                      </span>
                    </label>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <FinanceEmptyState
              inline
              state={FINANCE_EMPTY_STATES.noWithdrawalAccount}
              onRetry={() => navigate(FINANCE_PATH)}
              retryLabel="Go to Finance"
            />
          )}
        </div>
      </section>

      {recipientId && amount > 0 && (
        <section className="sy-flow-card">
          <div className="sy-flow-card-head">
            <h2 className="sy-flow-card-title">Summary</h2>
          </div>

          <div className="sy-flow-card-body">
            {withdrawal.evaluating ? (
              <SummaryLinesSkeleton />
            ) : withdrawal.feeError ? (
              <p className="fin-field-error">
                {serverMessage(withdrawal.feeError, FINANCE_COPY.feeFailed)}
              </p>
            ) : withdrawal.quote ? (
              <div className="fin-summary">
                <div className="fin-summary-line">
                  <span>Withdrawal Amount</span>
                  <b>{formatMoney(withdrawal.quote.gross)}</b>
                </div>

                <div className="fin-summary-line">
                  <span>Fees</span>
                  <b>{formatMoney(withdrawal.quote.fee)}</b>
                </div>

                <div className="fin-summary-line fin-summary-total">
                  <span>Received Amount</span>
                  <b>{formatMoney(withdrawal.quote.net)}</b>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      )}

      <div className="fin-sticky-bar">
        <button
          type="button"
          className="sy-flow-btn sy-flow-btn-primary"
          disabled={submitDisabled}
          onClick={handleSubmit}
        >
          {withdrawal.submitting ? "Requesting…" : "Request Withdrawal"}
        </button>
      </div>

      <AmountEntrySheet
        open={sheetOpen}
        mode="withdraw-accounts"
        balance={wallet.balance}
        maximumAmount={wallet.balance.value}
        onClose={() => setSheetOpen(false)}
        onSubmit={(value) => {
          setAmountOverride(value);
          setSheetOpen(false);
        }}
      />
    </FinanceShell>
  );
};

export default WithdrawRoute;
